import { WhereItIs } from "../components/WhereItIs";
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiErrorMessage } from "@/lib/api";
import { prov } from "@/lib/provenance";
import { AuthImg } from "@/components/AuthImg";

import { FloorActionButton } from "@/floor/FloorActionButton";
import { FloorScanOverlay } from "@/floor/FloorScanOverlay";
import { PrintLabelsLauncher } from "@/components/places/PrintLabelsLauncher";
import { useFlash } from "@/floor/useFlash";
import { useGoDesk } from "@/lib/floorReturn";
import { ArrowLeft, Boxes, PackageOpen, Package, Loader2, MapPin, ExternalLink } from "lucide-react";

// Bin / Container card — one card that branches on location type. A movable tote/pallet
// (type=container) can be MOVED; a fixed bin cannot. Both can have parts scanned INTO them.
// Replaces "Work a Bin" + "Move Container" for the floor (same backend endpoints).
export default function FloorLocationCard() {
  const { id } = useParams();
  const navigate = useNavigate();
  const goDesk = useGoDesk();
  const [loc, setLoc] = useState(null);
  const [contents, setContents] = useState(null);
  const [overlay, setOverlay] = useState(null); // 'add' | 'move'
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const { trigger, FlashOverlay } = useFlash();

  const load = async () => {
    try {
      const { data } = await api.get("/locations");
      const found = (data || []).find((l) => l.id === id);
      setLoc(found || false);
      api.get(`/locations/${id}/contents`).then((r) => setContents(r.data)).catch(() => setContents(null));
    } catch { setLoc(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const isContainer = loc && loc.type === "container";

  const doAddPart = async (partCode, meta) => {
    setBusy(true);
    try {
      const { data } = await api.post("/putaway", { part_scan: partCode, location_id: loc.short_code,
                                                    provenance: prov("floor_location_card", meta) });
      if (data.already) { trigger("warning", "ALREADY HERE"); }
      else if (!data.ok) { trigger("error", data.message || "FAILED"); return; }
      else { trigger("success", `IN · ${data.part?.short_code || partCode}`); }
      await load();
    } catch (e) { trigger("error", apiErrorMessage(e) || "FAILED"); }
    finally { setBusy(false); }
  };

  const doMove = async (destCode, meta) => {
    setBusy(true);
    try {
      const { data } = await api.post("/containers/move", { container_scan: loc.short_code, location_scan: destCode,
                                                            provenance: prov("floor_location_card", meta) });
      if (!data.ok) { trigger("error", data.message || "MOVE FAILED"); return; }
      trigger("success", `MOVED · ${data.moved_parts ?? 0} parts`); setOverlay(null); await load();
    } catch (e) { trigger("error", apiErrorMessage(e) || "MOVE FAILED"); }
    finally { setBusy(false); }
  };

  if (loc === false) return <NotFound onBack={() => navigate("/floor")} />;
  if (!loc) return <div className="flex-1 flex items-center justify-center"><Loader2 className="w-10 h-10 animate-spin text-slate-400" /></div>;

  const parts = contents?.parts || [];

  return (
    <div className="flex-1 flex flex-col" data-testid="floor-location-card">
      {FlashOverlay}
      <div className="p-4 sm:p-6 flex flex-col gap-5 flex-1">
        <button data-testid="floor-back" onClick={() => navigate("/floor")}
          className="flex items-center gap-1.5 font-bold text-[#B01E28] text-lg self-start">
          <ArrowLeft className="w-5 h-5" /> Scan
        </button>

        <div className="flex gap-4 items-center">
          <div className="w-24 h-24 rounded-xl border-2 border-slate-200 bg-slate-100 flex items-center justify-center shrink-0">
            {isContainer ? <Boxes className="w-11 h-11 text-slate-400" /> : <MapPin className="w-11 h-11 text-slate-400" />}
          </div>
          <div className="min-w-0">
            <div className="text-base font-bold uppercase tracking-[0.15em] text-slate-400">{isContainer ? "Tote / Pallet" : "Bin / Location"}</div>
            <div data-testid="floor-location-name" className="font-display text-3xl font-black tracking-tight leading-tight break-words">{loc.short_code}</div>
            {/* PLAIN WORDS INSIDE A HOLDER (his design, 21 Oct): "Slot 4 · Glass rack 2 · on Row 3" */}
            {loc.where_words?.line && loc.where_words.line !== loc.where_words.path && (
              <div data-testid="floor-location-plain" className="text-2xl font-black text-slate-900 leading-tight">{loc.where_words.line}</div>
            )}
            <WhereItIs location={loc} size="lg" icon={false} showCode={false} testid="floor-location-where" className="text-slate-500" />
            <div className="mt-1 text-lg font-bold text-slate-700" data-testid="floor-location-count">
              {contents ? `${contents.part_count} part${contents.part_count === 1 ? "" : "s"}` : "…"}
            </div>
          </div>
        </div>

        {/* contents preview — a short peek by default; tap to expand (full detail is a desk view) */}
        {parts.length > 0 && (
          <div data-testid="floor-location-contents">
            <div className="rounded-xl border-2 border-[#E2E8F0] bg-white divide-y divide-slate-100 overflow-hidden">
              {(expanded ? parts.slice(0, 50) : parts.slice(0, 5)).map((p) => (
                <button key={p.id} onClick={() => navigate(`/floor/part/${p.id}`)}
                  className="w-full flex items-center gap-3 p-3 text-left active:bg-slate-50">
                  <div className="w-12 h-12 rounded-lg bg-slate-100 overflow-hidden shrink-0">
                    {p.photo ? <AuthImg fileId={p.photo} thumb className="w-full h-full object-cover" /> : <Package className="w-6 h-6 text-slate-300 m-3" />}
                  </div>
                  <div className="min-w-0">
                    <div className="font-mono font-bold text-base truncate">{p.short_code}</div>
                    <div className="text-sm text-slate-500 truncate">{p.name || "—"}</div>
                  </div>
                </button>
              ))}
            </div>
            {parts.length > 5 && (
              <button data-testid="floor-location-expand" onClick={() => setExpanded((v) => !v)}
                className="mt-2 w-full h-12 rounded-xl border-2 border-[#E2E8F0] bg-white font-bold text-slate-600 active:scale-[0.99] transition-transform duration-100">
                {expanded ? "Show less" : `View all ${parts.length}`}
              </button>
            )}
          </div>
        )}

        {parts.length === 0 && contents && (
          <div className="rounded-xl border-2 border-dashed border-[#E2E8F0] bg-white p-5 text-center text-base font-semibold text-slate-400" data-testid="floor-location-empty">
            Empty — nothing stored here yet.
          </div>
        )}

        {/* ACTIONS */}
        <div className="mt-auto grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="floor-location-actions">
          <FloorActionButton testid="floor-action-loc-add" label="Add parts" icon="PackageOpen" tone="primary"
            busy={busy && overlay === null} onClick={() => setOverlay("add")} />
          {isContainer && (
            <FloorActionButton testid="floor-action-loc-move" label="Move this tote" icon="ArrowRightLeft" tone="secondary"
              busy={busy && overlay === null} onClick={() => setOverlay("move")} />
          )}
          <PrintLabelsLauncher locId={loc.id} button={({ onClick, label }) => (
            <FloorActionButton testid="floor-action-loc-print" label={label} icon="Printer" tone="secondary" onClick={onClick} />)} />
        </div>

        {/* Escape hatch — full desk location record (all contents + tools); returns to Floor */}
        <button data-testid="floor-location-full-detail" onClick={() => goDesk(`/scan/loc/${loc.short_code}`)}
          className="w-full h-12 rounded-xl border-2 border-slate-200 bg-white font-bold text-slate-700 flex items-center justify-center gap-2 active:scale-[0.99] transition-transform duration-100">
          <ExternalLink className="w-5 h-5 text-[#B01E28]" /> See Full Detail
        </button>
      </div>

      <FloorScanOverlay open={overlay === "add"} testid="floor-loc-add-overlay"
        title="Scan a part" sub={`Into ${loc.short_code}`} submitLabel="Add here" continuous
        onScan={doAddPart} onClose={() => setOverlay(null)} busy={busy} />
      <FloorScanOverlay open={overlay === "move"} testid="floor-loc-move-overlay"
        title="Scan destination" sub={`Move ${loc.short_code}`} submitLabel="Move here"
        onScan={doMove} onClose={() => setOverlay(null)} busy={busy} />
    </div>
  );
}

function NotFound({ onBack }) {
  return (
    <div className="flex-1 flex flex-col items-center justify-center gap-4 p-6" data-testid="floor-card-notfound">
      <div className="text-2xl font-black text-slate-700">This location wasn’t found.</div>
      <FloorActionButton label="Back to scan" icon="ScanLine" tone="primary" onClick={onBack} testid="floor-notfound-back" />
    </div>
  );
}
